import React from "react";
import {
  DeleteOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
} from "@ant-design/icons";
import { InputNumber, Select, notification } from "antd";
import trans from "../../../assets/icons/transition.png";
import { templateSelectOptions, filterSelectOptions } from "../../../../../static/selectsOptions";
import { useDispatch, useSelector } from "react-redux";
import { FoldersSelector } from "../../../../../store/selectors";
import { setImages, SetFolder } from "../../../../../store/slices/foldersSlice";
import { useFavorite } from "../../../../favourite/hooks/useFavorite";
import axiosInstance from "../../../../../helpers/axiosConfig";
import { file, collections } from "../../../../../static/api";

import asFav from "../../../../../assets/icons/hurt_fav.png";
import addFav from "../../../../../assets/icons/heart-black.png";
import timer from "../../../assets/icons/timer.png";
import duplicateIcon from "../../../assets/icons/duplicate.png";

import "./style.css";

export const ListCardSettings = ({ onRemove, card }) => {
  const dispatch = useDispatch();

  const { timePerSlide, id, isFavorite, rotation, template, filter } = card;

  const { folderItem, folderImages } = useSelector(FoldersSelector);
  const { addToFavorite, removeFromFav } = useFavorite();

  const initialSeconds = timePerSlide
    ? timePerSlide / 1000
    : folderItem.timePerSlide / 1000;
  const initialTemplate = template || folderItem?.template || "1up";
  const initialFilter = filter || "";

  const [addToFav, setAddToFav] = React.useState(isFavorite);
  const [time, setTime] = React.useState(initialSeconds);
  const [templateValue, setTemplateValue] = React.useState(initialTemplate);
  const [filterValue, setFilterValue] = React.useState(initialFilter);

  // Sync state when card prop changes (e.g., after reload from backend)
  React.useEffect(() => {
    const newInitialTemplate = template || folderItem?.template || "1up";
    const newInitialFilter = filter || "";
    setTemplateValue(newInitialTemplate);
    setFilterValue(newInitialFilter);
    setAddToFav(isFavorite);
    if (timePerSlide) {
      setTime(timePerSlide / 1000);
    } else if (folderItem?.timePerSlide) {
      setTime(folderItem.timePerSlide / 1000);
    }
  }, [card, template, filter, isFavorite, timePerSlide, folderItem?.template, folderItem?.timePerSlide]);

  const handleInputChange = async (value) => {
    setTime((prevState) => value);
    await axiosInstance.patch(`${file}/${id}`, {
      timePerSlide: value * 1000,
    });
    const newArr = folderImages.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          timePerSlide: value * 1000,
        };
      } else {
        return item;
      }
    });
    dispatch(setImages(newArr));
  };

  const handleTemplate = async (value) => {
    setTemplateValue((prevState) => value);
    try {
      await axiosInstance.patch(`${file}/${id}`, {
        template: value,
      });
      const newArr = folderImages.map((item) => {
        if (item.id === id) {
          return {
            ...item,
            template: value,
          };
        } else {
          return item;
        }
      });
      dispatch(setImages(newArr));
    } catch (error) {
      console.error('Error updating template:', error);
      // Revert the template value on error
      setTemplateValue((prevState) => template || folderItem?.template || "1up");
      
      const is502Error = error.response?.status === 502 || error.code === 'ECONNREFUSED';
      const errorMessage = is502Error 
        ? 'Unable to connect to server. Please check your connection and try again.'
        : error.response?.data?.message || error.message || 'Failed to update template';
      
      notification.error({
        message: 'Template Update Failed',
        description: errorMessage,
        duration: 5,
      });
    }
  };

  const handleFilter = async (value) => {
    setFilterValue((prevState) => value);
    await axiosInstance.patch(`${file}/${id}`, {
      filter: value || null,
    });
    const newArr = folderImages.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          filter: value || null,
        };
      } else {
        return item;
      }
    });
    dispatch(setImages(newArr));
  };

  const rotateRight = async () => {
    // rotation comes from the API as a string (varchar) — parse to number first
    let newRotation = (parseInt(rotation) || 0) + 90;
    if (newRotation >= 360) {
      newRotation = 0;
    }
    const rotationStr = String(newRotation);
    try {
      await axiosInstance.patch(`${file}/${id}`, {
        rotation: rotationStr,
      });
    } catch (error) {
      console.error('Error rotating item:', error);
      return;
    }
    const newArr = folderImages.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          rotation: rotationStr,
        };
      } else {
        return item;
      }
    });
    dispatch(setImages(newArr));
  };

  const rotateLeft = async () => {
    let newRotation = (parseInt(rotation) || 0) - 90;
    if (newRotation <= -360) {
      newRotation = 0;
    }
    const rotationStr = String(newRotation);
    try {
      await axiosInstance.patch(`${file}/${id}`, {
        rotation: rotationStr,
      });
    } catch (error) {
      console.error('Error rotating item:', error);
      return;
    }
    const newArr = folderImages.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          rotation: rotationStr,
        };
      } else {
        return item;
      }
    });
    dispatch(setImages(newArr));
  };

  const handleAddToFav = async () => {
    await addToFavorite(id);
    setAddToFav(true);
  };
  const handleRemoveFromFav = async () => {
    await removeFromFav(id);
    setAddToFav(false);
  };

  const handleDuplicate = async () => {
    try {
      const response = await axiosInstance.post(`${file}/${id}/duplicate`);
      const newFile = response.data;
      
      // Find the index of the current item
      const currentIndex = folderImages.findIndex((item) => item.id === id);
      
      if (currentIndex === -1) {
        console.error('Current item not found');
        notification.error({
          message: 'Duplicate Failed',
          description: 'Could not find the item to duplicate.',
        });
        return;
      }
      
      // Insert the new file right after the current item
      const newArr = [
        ...folderImages.slice(0, currentIndex + 1),
        newFile,
        ...folderImages.slice(currentIndex + 1),
      ];
      
      // Update ranks in backend FIRST to maintain order
      const ranks = newArr.map((gif) => gif.id);
      await axiosInstance.patch(`${collections}/${folderItem.id}`, {
        ranks,
      });
      
      // Update folderItem with new ranks so normalizeCollectionFiles uses correct order
      const updatedFolderItem = {
        ...folderItem,
        ranks,
      };
      dispatch(SetFolder(updatedFolderItem));
      
      // Update local state after ranks are updated
      dispatch(setImages(newArr));

      notification.success({
        message: 'Item Duplicated',
        description: 'The item has been successfully duplicated.',
      });
    } catch (error) {
      console.error('Error duplicating file:', error);
      
      const is502Error = error.response?.status === 502 || error.code === 'ECONNREFUSED';
      const errorMessage = is502Error 
        ? 'Unable to connect to server. Please check your connection and try again.'
        : error.response?.data?.message || error.message || 'Failed to duplicate item';
      
      notification.error({
        message: 'Duplicate Failed',
        description: errorMessage,
        duration: 5,
      });
    }
  };

  return (
    <div className={"list_card_settings"}>
      {addToFav ? (
        <img
          className={"add__fav"}
          src={asFav}
          width={24}
          height={24}
          alt="hart_add"
          onClick={handleRemoveFromFav}
        />
      ) : (
        <img
          className={"add__fav"}
          src={addFav}
          width={24}
          height={24}
          alt="hurt_fav"
          onClick={handleAddToFav}
        />
      )}

      <div>
        <RotateLeftOutlined className={"icons"} onClick={rotateLeft} />
        <RotateRightOutlined
          className={"icons"}
          style={{ marginLeft: 15 }}
          onClick={rotateRight}
        />
      </div>

      <div className={"list_card_settings_box"}>
        <div className={"list_card_settings_field"}>
          <div className={"list_card_settings_label"}>
            <img src={timer} alt={timer} />
          </div>
          <InputNumber
            value={time}
            onChange={handleInputChange}
            style={{ width: "55px" }}
            max={15}
            min={2}
            formatter={(value) => value ? `${value}s` : ''}
            parser={(value) => value ? value.replace('s', '') : ''}
          />
        </div>
      </div>

      <div className={"list_card_settings_box"}>
        <div className={"list_card_settings_field"}>
          <div className={"list_card_settings_label"}>
            <img src={trans} alt={"template"} />
            <p>Template:</p>
          </div>
          <Select
            value={templateValue}
            style={{ width: 126 }}
            onChange={handleTemplate}
            options={templateSelectOptions}
          />
        </div>
      </div>

      <div className={"list_card_settings_box"}>
        <div className={"list_card_settings_field"}>
          <div className={"list_card_settings_label"}>
            <img src={trans} alt={"filter"} />
            <p>Filter:</p>
          </div>
          <Select
            value={filterValue}
            style={{ width: 126 }}
            onChange={handleFilter}
            options={filterSelectOptions}
          />
        </div>
      </div>

      <img
        src={duplicateIcon}
        alt="duplicate"
        className={"icons"}
        onClick={handleDuplicate}
        style={{ width: 24, height: 24, cursor: "pointer" }}
      />
      <DeleteOutlined onClick={onRemove} className={"icons"} />
    </div>
  );
};
